import React, { useCallback, useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../common/Loader";
import {
  PlatformTeamDetail,
  PlatformTeamInvite,
} from "../../common/platformTypes";
import { Date as DateView } from "../../components/Date";
import { PlatformUserLink } from "../../components/DocumentLink";
import { DocumentTabNav } from "../../components/DocumentTabNav";
import MenuBar from "../../components/MenuBar";
import { PlatformSubscriptionsTable } from "../../components/PlatformSubscriptionsTable";
import { ToolbarButton } from "../../components/ToolbarButton";
import {
  DocumentTable,
  DocumentTableProperty,
} from "../../components/DocumentTable";
import NoDataMessage from "../../common/NoDataMessage";
import { subscriptionCreate, teamInfo } from "../../services/api/Platform";
import { TeamMembersTab } from "./components/TeamMembersTab";
import { CreatePlatformSubscriptionModal } from "../PlatformSubscription/components/CreatePlatformSubscriptionModal";

type Tab = "details" | "users" | "invites" | "subscriptions";

export const TeamPage: React.FunctionComponent<
  RouteComponentProps<{ teamId: string }>
> = ({ match }) => {
  const teamId = match.params.teamId;
  const [tab, setTab] = useState<Tab>("details");
  const [detail, setDetail] = useState<PlatformTeamDetail>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await teamInfo(teamId));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col p-0">
          <MenuBar />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="card mt-2 mb-3">
            <div className="card-header pb-3">
              <h2 className="card-title mb-0">
                {detail ? detail.team.name : "Team"}
              </h2>
              <small className="text-monospace text-muted">{teamId}</small>
            </div>
          </div>

          <DocumentTabNav
            initialTab="details"
            tabs={[
              {
                id: "details",
                label: "Details",
                onClick: () => setTab("details"),
              },
              { id: "users", label: "Users", onClick: () => setTab("users") },
              {
                id: "invites",
                label: "Invites",
                onClick: () => setTab("invites"),
              },
              {
                id: "subscriptions",
                label: "Subscriptions",
                onClick: () => setTab("subscriptions"),
              },
            ]}
          />

          {detail && !loading && !error && tab === "subscriptions" && (
            <CreateSubscriptionToolbar
              hasSubscription={detail.subscriptions.length > 0}
              onCreateClick={() => setCreateOpen(true)}
            />
          )}

          {detail && !loading && !error && tab === "users" ? (
            <TeamMembersTab
              teamId={detail.team._id}
              members={detail.members}
              licenses={detail.subscriptions[0]?.licenses}
              reload={load}
            />
          ) : (
            <div className="card bg-dark mt-2">
              {loading ? (
                <Loader />
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : !detail ? (
                <div className="alert alert-warning mb-0">Team not found</div>
              ) : tab === "details" ? (
                <Details detail={detail} />
              ) : tab === "invites" ? (
                <Invites invites={detail.invites} />
              ) : (
                <PlatformSubscriptionsTable
                  subscriptions={detail.subscriptions}
                  hideTeamColumn
                />
              )}
            </div>
          )}
        </div>
      </div>
      {detail && (
        <CreatePlatformSubscriptionModal
          isOpen={createOpen}
          teamId={detail.team._id}
          teamName={detail.team.name}
          onClose={() => setCreateOpen(false)}
          // Closes only once the create has landed: a refusal is re-thrown so
          // the modal can show it with the form still filled in.
          onSubmit={async (params) => {
            await subscriptionCreate({ teamId: detail.team._id, ...params });
            setCreateOpen(false);
            toast.success("Subscription created");
            await load();
          }}
        />
      )}
    </div>
  );
};

const ONE_SUBSCRIPTION_PER_TEAM =
  "A team can have only one subscription at a time. Update or cancel the existing one instead.";

/**
 * Always rendered, disabled once the team has one: hiding it made the action
 * look missing rather than unavailable. Platform has a partial unique index
 * on teamId, so a second subscription cannot exist.
 *
 * A disabled button fires no events of its own, so the reason it is disabled
 * has to come from the span wrapping it — which is what makes hovering and
 * clicking the button itself explain the state.
 */
const CreateSubscriptionToolbar: React.FunctionComponent<{
  hasSubscription: boolean;
  onCreateClick: () => void;
}> = ({ hasSubscription, onCreateClick }) => (
  <div className="d-flex align-items-center mt-3">
    <span
      id="create-subscription-toolbar"
      className="d-flex align-items-center"
      onClick={
        hasSubscription
          ? () => toast.info(ONE_SUBSCRIPTION_PER_TEAM)
          : undefined
      }
      style={hasSubscription ? { cursor: "pointer" } : undefined}
    >
      <div className="btn-group">
        <ToolbarButton
          // Grey when it cannot be used: a disabled green button still reads
          // as the primary action on the page. Safe on this light background
          // — the same class is invisible against a dark card.
          className={
            hasSubscription ? "btn-outline-secondary" : "btn-outline-success"
          }
          icon="fa-plus-circle"
          title="Create Subscription"
          disabled={hasSubscription}
          onClick={onCreateClick}
          style={hasSubscription ? { pointerEvents: "none" } : undefined}
        />
      </div>
      {hasSubscription && <i className="fas fa-info-circle text-muted ml-2" />}
    </span>
    {hasSubscription && (
      <UncontrolledTooltip target="create-subscription-toolbar">
        {ONE_SUBSCRIPTION_PER_TEAM}
      </UncontrolledTooltip>
    )}
  </div>
);

const Details: React.FunctionComponent<{ detail: PlatformTeamDetail }> = ({
  detail,
}) => {
  const { team } = detail;
  return (
    <table className="table table-dark table-sm table-hover mb-0">
      <tbody>
        <tr>
          <th style={{ width: "18%" }}>Id</th>
          <td className="text-monospace">{team._id}</td>
        </tr>
        <tr>
          <th>Name</th>
          <td>{team.name}</td>
        </tr>
        <tr>
          <th>Type</th>
          <td className="text-capitalize">{team.type}</td>
        </tr>
        <tr>
          <th>Member Count</th>
          <td>{team.memberCount}</td>
        </tr>
        <tr>
          <th>Created By</th>
          <td>
            <div className="d-flex flex-wrap align-items-baseline">
              <PlatformUserLink docId={team.createdBy}>
                {team.createdByName ?? team.createdBy}
              </PlatformUserLink>
              {team.createdByEmail && (
                <span className="text-muted ml-2">{team.createdByEmail}</span>
              )}
            </div>
          </td>
        </tr>
        <tr>
          <th>Created</th>
          <td>
            <DateView date={new Date(team.createdAt)} />
          </td>
        </tr>
      </tbody>
    </table>
  );
};

const INVITE_PROPERTIES: DocumentTableProperty<PlatformTeamInvite>[] = [
  { title: "Email", render: (d) => d.email },
  {
    title: "Role",
    render: (d) => <span className="text-capitalize">{d.role}</span>,
  },
  {
    title: "Status",
    // A pending invite past its expiry is the usual answer to "why can't
    // they join?", so it must not read the same as a live one.
    render: (d) => {
      const stale =
        d.status === "pending" && new Date(d.expiresAt) < new Date();
      return (
        <span
          className={
            d.status === "pending" && !stale
              ? "text-info"
              : d.status === "accepted"
              ? "text-success"
              : "text-muted"
          }
        >
          {stale ? "expired" : d.status}
        </span>
      );
    },
  },
  {
    title: "Invited By",
    render: (d) => (
      <PlatformUserLink docId={d.invitedBy} scope={d._id}>
        {d.invitedByName ?? d.invitedBy}
      </PlatformUserLink>
    ),
  },
  {
    title: "Sent",
    render: (d) => <DateView date={new Date(d.createdAt)} />,
  },
  {
    title: "Expires",
    render: (d) => <DateView date={new Date(d.expiresAt)} />,
  },
];

const Invites: React.FunctionComponent<{ invites: PlatformTeamInvite[] }> = ({
  invites,
}) => {
  if (!invites.length) {
    return <NoDataMessage componentName="invite" />;
  }
  return (
    <DocumentTable<PlatformTeamInvite>
      objects={invites}
      properties={INVITE_PROPERTIES}
    />
  );
};
